import React, { useState, useEffect } from 'react';
import { taskService } from '../api/taskService';
import { getDaysDifference } from '../utils/dateUtils';

const TaskProgress = ({ tasks = [] }) => {
    const [subtasksCache, setSubtasksCache] = useState({});

    // 1. Filter active tasks
    const activeTasks = tasks.filter(t => t.Status !== 'Completed' && t.Status !== 'Cancelled');

    // 2. Sort: Priority (High->Low) then Date (Soon->Late)
    const priorityOrder = { 'High': 1, 'Medium': 2, 'Low': 3, 'Normal': 3 };
    activeTasks.sort((a, b) => {
        const pA = priorityOrder[a.Priority] || 3;
        const pB = priorityOrder[b.Priority] || 3;
        if (pA !== pB) return pA - pB;
        return new Date(a.EndDate || a.DueDate || '9999-12-31') - new Date(b.EndDate || b.DueDate || '9999-12-31');
    });

    // 3. Smart Fill Logic (Max 5 items total)
    const MAX_ITEMS = 5;
    const dueToday = [];

    for (const t of activeTasks) {
        const days = getDaysDifference(t.EndDate || t.DueDate);
        if (days === 0) {
            dueToday.push(t);
        }
    }

    // Determine what to show
    let displayGroups = [];
    if (dueToday.length > 0) {
        const take = Math.min(dueToday.length, MAX_ITEMS);
        displayGroups.push({ title: "Due Today", items: dueToday.slice(0, take), color: "#dc2626" });
    }

    // Prepare displayed items list
    const displayedItems = displayGroups.flatMap(g => g.items);
    const displayedTaskIdsString = displayedItems.map(t => t.TaskID).sort().join(',');

    useEffect(() => {
        let isMounted = true;

        const fetchMissingSubtasks = async () => {
            const missing = displayedItems.filter(t =>
                (typeof t.TotalSubTasks !== 'number' || typeof t.CompletedSubTasks !== 'number')
                && !subtasksCache[t.TaskID]
            );

            if (missing.length === 0) return;

            const toSubtaskArray = (value) => {
                if (Array.isArray(value)) return value;
                if (value && Array.isArray(value.items)) return value.items;
                if (value && Array.isArray(value.data)) return value.data;
                if (value && Array.isArray(value.Subtasks)) return value.Subtasks;
                return [];
            };

            const updates = {};
            let changed = false;
            for (const t of missing) {
                try {
                    const rawSubs = await taskService.getSubTasks(t.TaskID);
                    console.log(`[TaskProgress] rawSubs for task ${t.TaskID}:`, rawSubs);
                    const subs = toSubtaskArray(rawSubs);
                    console.log(`[TaskProgress] parsed subs for task ${t.TaskID}:`, subs);
                    updates[t.TaskID] = {
                        TotalSubTasks: subs.length,
                        CompletedSubTasks: subs.filter(s => s.Status === 'Completed').length
                    };
                    changed = true;
                } catch (e) {
                    console.error("Failed to fetch subtasks for TaskProgress", e);
                }
            }
            console.log("[TaskProgress] Hydration updates:", updates);

            if (isMounted && changed) {
                setSubtasksCache(prev => ({ ...prev, ...updates }));
            }
        };

        fetchMissingSubtasks();

        return () => { isMounted = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [displayedTaskIdsString]);

    const renderTaskGroup = (group) => {
        return (
            <div key={group.title} style={{ marginBottom: '1.5rem' }}>
                <h5 style={{ margin: '0 0 0.5rem 0', color: group.color, fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    {group.title}
                </h5>
                {group.items.map(task => {
                    // Use subtasks if available for real progress tracking
                    let totalSub = 0;
                    let completedSub = 0;
                    const cached = subtasksCache[task.TaskID];

                    if (cached) {
                        totalSub = cached.TotalSubTasks;
                        completedSub = cached.CompletedSubTasks;
                    } else if (typeof task.TotalSubTasks === 'number' && typeof task.CompletedSubTasks === 'number') {
                        totalSub = task.TotalSubTasks;
                        completedSub = task.CompletedSubTasks;
                    } else if (Array.isArray(task.Subtasks)) {
                        totalSub = task.Subtasks.length;
                        completedSub = task.Subtasks.filter(s => s.Status === 'Completed').length;
                    }

                    // Progress Calculation
                    let progress = 0;
                    if (task.Status === 'Completed') {
                        progress = 100;
                    } else if (totalSub > 0) {
                        progress = Math.round((completedSub / totalSub) * 100);
                    } else {
                        // Fallback when there are no subtasks
                        const statusProgressMap = {
                            'New': 10,
                            'Open': 10,
                            'Pending': 20,
                            'In Progress': 50,
                            'Review': 80,
                            'Completed': 100,
                            'Cancelled': 0
                        };
                        progress = statusProgressMap[task.Status] || 0;
                    }

                    // Progress Bar Color
                    let barColor = '#3b82f6';
                    if (task.Priority === 'High') barColor = '#ef4444';
                    else if (task.Priority === 'Medium') barColor = '#f59e0b';

                    return (
                        <div key={task.TaskID} style={{ marginBottom: '1rem' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem', alignItems: 'center' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', maxWidth: '80%' }}>
                                    <span style={{
                                        width: '8px', height: '8px', borderRadius: '50%',
                                        backgroundColor: barColor, flexShrink: 0
                                    }}></span>
                                    <span 
                                        title={task.Title}
                                        style={{
                                            fontWeight: 600, fontSize: '0.9rem', color: '#334155',
                                            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'
                                        }}
                                    >
                                        {task.Title}
                                    </span>
                                </div>
                                <span style={{ fontSize: '0.8rem', fontWeight: 600, color: barColor }}>
                                    {progress}%
                                </span>
                            </div>
                            <div style={{ width: '100%', height: '6px', backgroundColor: '#e2e8f0', borderRadius: '3px', overflow: 'hidden' }}>
                                <div style={{
                                    width: `${progress}%`,
                                    height: '100%',
                                    backgroundColor: barColor,
                                    borderRadius: '3px',
                                    transition: 'width 0.5s ease-in-out'
                                }} />
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
            {displayGroups.length === 0 ? (
                <p style={{ color: '#64748b', fontStyle: 'italic', fontSize: '0.9rem' }}>No critical tasks due today.</p>
            ) : (
                displayGroups.map(group => renderTaskGroup(group))
            )}
        </div>
    );
};

export default TaskProgress;
